import { atom, computed } from 'nanostores'

export const fromPostId = atom('')
/**
 * The structure is a record where each key is a string (typically a tab category or group),
 * and the value is an array of strings representing the identifiers or slugs for each tab within that group.
 *
 * Example:
 * {
 *   "discover": ["recommended", "hot", "others_1"],
 *   "mall": ["express_checkout_daily_products", "express_checkout_other_1"]
 * }
 */
export const $tabsListStructure = atom<Record<string, string[]>>({})
/**
 * Each item includes:
 *   - route: the path string used in routing for the tab (e.g. '/explore')
 *   - slug: a unique identifier string for the tab (e.g. 'express_checkout_daily_products')
 *
 * This array is used to map between routes and slugs, facilitating tab navigation and lookups.
 */
export const $tabsListDetail = atom<{ route: string; slug: string }[]>([])

/**
 * A list of valid routes from the current $tabsListDetail.
 * If route is not in the list, it will be redirected to the recommended page.
 */
export const $validRouteList = computed($tabsListDetail, tabsListDetail => {
	return tabsListDetail.map(tab => tab.route)
})

export function setFromPostId(value: string) {
	fromPostId.set(value)
}

export function setTabsListStructure(value: Record<string, string[]>) {
	$tabsListStructure.set(value)
}

export function setTabsListDetail(value: { route: string; slug: string }[]) {
	$tabsListDetail.set(value)
}

/**
 * Retrieves the slug associated with the given route from the $tabsListDetail atom.
 *
 * @param {string} route - The route for which to find the corresponding slug.
 * @returns {string} The slug corresponding to the given route, or an empty string if not found.
 */
export function getSlugByRoute(route: string): string {
	return $tabsListDetail.value?.find(item => item.route === route)?.slug || ''
}

/**
 * Retrieves the route associated with the given slug from the $tabsListDetail atom.
 *
 * @param {string} slug - The slug for which to find the corresponding route.
 * @returns {string} The route corresponding to the given slug, or an empty string if not found.
 */
export function getRouteBySlug(slug: string): string {
	return $tabsListDetail.value?.find(item => item.slug === slug)?.route || ''
}
